"use client";
import { motion } from "framer-motion";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";

// The full product screencast (18 min) lives on S3: too big for the repo and the Vercel deployment.
const VIDEO_SRC =
  "https://reputationsritter-media-bucket-2.s3.eu-central-1.amazonaws.com/saas-landing/screencast-coldfire-2026-10-26.mp4";
const POSTER_SRC = "/hero-demo-poster.jpg";

export const HeroVideo = () => {
  // No autoplay: the poster card starts the video with sound on click and comes back when it ends.
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const play = () => {
    setPlaying(true);
    void videoRef.current?.play();
  };

  const reset = () => {
    setPlaying(false);
    if (videoRef.current) videoRef.current.currentTime = 0;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut", delay: 0.3 }}
      className="relative w-full max-w-6xl mx-auto px-4 mt-2 md:mt-4"
    >
      {/* Brand glow behind the panel */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-10 top-10 bottom-0 -z-10 rounded-[3rem] bg-red-600/25 blur-3xl"
      />

      <div className="relative aspect-video overflow-hidden rounded-2xl md:rounded-3xl bg-neutral-950 ring-1 ring-white/10 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)]">
        {/* cover fills the 16:9 panel; in fullscreen the screen's own aspect ratio must not crop the video */}
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover [&:fullscreen]:object-contain [&:-webkit-full-screen]:object-contain"
          src={VIDEO_SRC}
          poster={POSTER_SRC}
          controls={playing}
          playsInline
          preload="none"
          onEnded={reset}
        />
        {!playing && <PosterOverlay onPlay={play} />}
      </div>
    </motion.div>
  );
};

const PosterOverlay = ({ onPlay }: { onPlay: () => void }) => {
  const t = useTranslations("hero");
  return (
    <button
      type="button"
      onClick={onPlay}
      aria-label={t("video_play")}
      className="group absolute inset-0 block text-left"
    >
      <Image
        src={POSTER_SRC}
        alt=""
        fill
        priority
        sizes="(max-width: 1200px) 100vw, 1152px"
        className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
      />
      <span
        aria-hidden
        className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/5"
      />

      <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-4 md:p-8">
        <span className="flex items-center gap-3 md:gap-5">
          <span
            className="flex h-11 w-11 md:h-16 md:w-16 shrink-0 items-center justify-center rounded-full bg-red-600 ring-4 ring-red-500/25 transition-transform duration-300 group-hover:scale-110"
            style={{ boxShadow: "0 0 40px rgba(239,68,68,0.55)" }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 md:h-7 md:w-7 text-white ml-0.5 md:ml-1" aria-hidden>
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
          <span className="block max-w-xl">
            <span className="block text-sm sm:text-base md:text-3xl font-bold tracking-tight text-white">
              {t("video_title")}
            </span>
            <span className="mt-1 hidden md:block text-base text-neutral-300">
              {t("video_subtitle")}
            </span>
          </span>
        </span>
        <span className="shrink-0 rounded-full bg-white/10 px-3 py-1 text-xs md:text-sm font-semibold tabular-nums text-white ring-1 ring-white/15 backdrop-blur">
          {t("video_duration")}
        </span>
      </span>
    </button>
  );
};
